import { apiSlice } from "./apiSlice";

// Mirrors emmi-backend/src/data-questions/data-questions.types.ts.

export type QueryCell = string | number | boolean | null;

export interface QueryResult {
  columns: string[];
  rows: Array<Record<string, QueryCell>>;
  rowCount: number;
  truncated: boolean;
  totalCount: number | null;
  durationMs: number;
}

export type DataQuestionStep =
  | { kind: "thinking"; text: string; at: number }
  | { kind: "sql"; text: string; sql: string; at: number }
  | {
      kind: "executed";
      text: string;
      sql: string;
      rowCount: number;
      durationMs: number;
      at: number;
    }
  | { kind: "retry"; text: string; error: string; at: number }
  | { kind: "count"; text: string; totalCount: number; at: number }
  | { kind: "answer"; text: string; at: number };

export type DataQuestionStatus = "OK" | "ERROR" | "CLARIFY";
export type DataQuestionFeedback = "UP" | "DOWN";

export interface DataQuestionTurn {
  turnId: number;
  threadId: number;
  question: string;
  status: DataQuestionStatus;
  answer: string | null;
  sql: string | null;
  assumptions: string[];
  followUps: string[];
  result: QueryResult | null;
  steps: DataQuestionStep[];
  error: string | null;
  durationMs: number;
  model: string;
  createdAt: string;
  feedback: DataQuestionFeedback | null;
  savedName: string | null;
}

export type DataQuestionStreamEvent =
  | { type: "thread"; threadId: number }
  | { type: "step"; step: DataQuestionStep }
  | { type: "result"; result: DataQuestionTurn }
  | { type: "error"; message: string; code: string };

export interface DataQuestionsStatus {
  enabled: boolean;
  model: string;
  maxRows: number;
}

export interface CatalogColumn {
  name: string;
  type: string;
  description: string;
}

export interface CatalogView {
  name: string;
  description: string;
  columns: CatalogColumn[];
}

export interface DataQuestionsCatalog {
  views: CatalogView[];
  /** column name -> back-office route with `{id}` placeholder */
  links: Record<string, string>;
  glossary: Array<{ term: string; meaning: string }>;
  suggestions: string[];
}

export interface DataQuestionThreadSummary {
  threadId: number;
  title: string;
  turns: number;
  createdAt: string;
  updatedAt: string;
}

export interface DataQuestionThread {
  threadId: number;
  title: string;
  userId: number;
  createdAt: string;
  updatedAt: string;
  turns: DataQuestionTurn[];
}

const BASE = "/admin/data-questions";

export const dataQuestionsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDataQuestionsStatus: builder.query<DataQuestionsStatus, void>({
      query: () => ({ url: `${BASE}/status` }),
    }),
    getDataQuestionsCatalog: builder.query<DataQuestionsCatalog, void>({
      query: () => ({ url: `${BASE}/catalog` }),
    }),
    listDataQuestionThreads: builder.query<DataQuestionThreadSummary[], void>({
      query: () => ({ url: `${BASE}/threads` }),
      providesTags: [{ type: "DataQuestion", id: "THREADS" }],
    }),
    getDataQuestionThread: builder.query<DataQuestionThread, number>({
      query: (threadId) => ({ url: `${BASE}/threads/${threadId}` }),
      providesTags: (_result, _error, threadId) => [
        { type: "DataQuestion", id: threadId },
      ],
    }),
    listSavedDataQuestions: builder.query<DataQuestionTurn[], void>({
      query: () => ({ url: `${BASE}/saved` }),
      providesTags: [{ type: "DataQuestion", id: "SAVED" }],
    }),
    setDataQuestionFeedback: builder.mutation<
      DataQuestionTurn,
      { turnId: number; feedback: DataQuestionFeedback; comment?: string }
    >({
      query: ({ turnId, ...data }) => ({
        url: `${BASE}/turns/${turnId}/feedback`,
        method: "POST",
        data,
      }),
      // The thread cache must not hand back the pre-feedback snapshot when
      // the admin reopens the conversation.
      invalidatesTags: (result) =>
        result ? [{ type: "DataQuestion", id: result.threadId }] : [],
    }),
    saveDataQuestion: builder.mutation<
      DataQuestionTurn,
      { turnId: number; name: string }
    >({
      query: ({ turnId, name }) => ({
        url: `${BASE}/turns/${turnId}/save`,
        method: "POST",
        data: { name },
      }),
      invalidatesTags: (result) => [
        { type: "DataQuestion", id: "SAVED" },
        ...(result ? [{ type: "DataQuestion" as const, id: result.threadId }] : []),
      ],
    }),
    unsaveDataQuestion: builder.mutation<DataQuestionTurn, number>({
      query: (turnId) => ({
        url: `${BASE}/turns/${turnId}/save`,
        method: "DELETE",
      }),
      invalidatesTags: (result) => [
        { type: "DataQuestion", id: "SAVED" },
        ...(result ? [{ type: "DataQuestion" as const, id: result.threadId }] : []),
      ],
    }),
    rerunDataQuestion: builder.mutation<QueryResult, number>({
      query: (turnId) => ({
        url: `${BASE}/turns/${turnId}/rerun`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetDataQuestionsStatusQuery,
  useGetDataQuestionsCatalogQuery,
  useListDataQuestionThreadsQuery,
  useGetDataQuestionThreadQuery,
  useListSavedDataQuestionsQuery,
  useSetDataQuestionFeedbackMutation,
  useSaveDataQuestionMutation,
  useUnsaveDataQuestionMutation,
  useRerunDataQuestionMutation,
} = dataQuestionsApi;
